import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './index.css';
import { PPInput, PPModal, PPTextArea, PPTextField, PPTooltip } from 'processor-plataform-ui';
import { AccordionDetails, Box, Button, Step, StepButton, StepLabel, Stepper, TextField, Tooltip, makeStyles } from '@material-ui/core';
import PPDropDown from '../../PPDropDown';
import PPCheckBox from '../PPCheckBox';
import LcLoading from '../../Generic/LcLoading';
import LcAccordion from '../../Generic/LcAccordion';

export interface Option {
    label: string,
    value: string | number,
    disabled?: boolean
}

interface ConsultaCEP {
    cep: string,
    logradouro: string,
    complemento: string,
    bairro: string,
    localidade: string,
    uf: string,
    ibge: string,
    gia: string,
    ddd: string,
    siafi: string
}
interface FieldValidation {
    regex: RegExp,
    regexMsg: string
}

export interface Field {
    type: "text" | "password" | "number" | "CEP" | "checkbox" | "dropdown" | "dropdown-multi" | "textarea",
    name: string,
    label: string,
    required: boolean,
    hidden: boolean,
    disabled: boolean,
    options?: Option[],
    addressFieldFromCEP?: "logradouro" | "bairro" | "localidade" | "uf",
    tooltip?: string,
    validation?: FieldValidation,
    width?: number | string,
    blockEdit?: boolean,
    handleMenuOpen?: (inputValue: string) => Promise<any[]>
}

export interface FieldGroup {
    label?: string,
    accordion: boolean,
    fields: Field[]
}

export interface FormFields {
    id?: string,
    fieldgroups: FieldGroup[]
}

interface FormModalProps {
    mode: "create" | "edit" | "view",
    type: "fieldset" | "wizard",
    title: string,
    desc?: string,
    size: "small" | "medium" | "large",
    show: boolean,
    data: object,
    formFields: FormFields,
    onOpen: () => void | Promise<void>,
    onSave: (data: any) => void,
    onCancel: () => void,
    onClose: () => void
}

const PPFormModal: React.FC<FormModalProps> = (props) => {
    const [dirtyFields, setDirtyFields] = useState({});
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingCEP, setLoadingCEP] = useState<boolean>(false);
    const [formData, setFormData] = useState<object>(props.data);
    const [formValidation, setFormValidation] = useState<{ valid: boolean, help: string }[]>([]);
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [timeoutToken, setTimeoutToken] = useState<NodeJS.Timeout>();
    const [activeStep, setActiveStep] = useState<number>(0);

    const validate = () => {
        let _allFields: Field[] = [];
        let _formValidation = { ...formValidation };

        props.formFields.fieldgroups.forEach(g => g.fields.forEach(f => _allFields.push(f)));

        _allFields.forEach((field: Field, fi: number) => {
            // Initialize validation status for the field
            _formValidation[field.name] = { valid: true, help: "" };

            // Only validate if the field is dirty or if the form is being submitted (which we'll check later)
            if (dirtyFields[field.name] || props.mode === 'edit') {
                let isValid = true;
                let helpText = "";

                // Check regex validation if applicable
                if (field.validation) {
                    const regex = new RegExp(field.validation.regex);
                    isValid = regex.test(String(formData[field.name]));
                    helpText = isValid ? "" : field.validation.regexMsg;
                }

                // Check required fields
                if (field.required && !formData[field.name]) {
                    isValid = false;
                    helpText = "Campo obrigatório!";
                }

                _formValidation[field.name] = { valid: isValid, help: helpText };
            }
        });

        // Check if form is overall valid
        let _allValidations = Object.values(_formValidation);
        let _allValid = _allValidations.map(fvk => fvk['valid']).reduce((a, b) => { return a && b }, true);
        // let _allValid = Object.values(_formValidation).every(val => val.valid);

        setFormIsValid(_allValid);
        setFormValidation(_formValidation);
    };

    const handleFormSave = () => {
        // Mark all fields as dirty and validate
        const newDirtyFields = props.formFields.fieldgroups.reduce((acc, group) => {
            group.fields.forEach(field => {
                acc[field.name] = true;
            });
            return acc;
        }, {});

        setDirtyFields(newDirtyFields);

        // You might need to delay validation until state update completes
        setTimeout(() => {
            validate();
            // If form is valid, proceed with save
            if (formIsValid) {
                props.onSave(formData);
                handleModalClose();
            } else {
                alert("Please correct the errors before saving.");
            }
        }, 0);
    };
    useEffect(validate, [formData]);
    const validateCurrentStep = () => {
        const currentFields = props.formFields.fieldgroups[activeStep]?.fields || [];
        let _formValidation = { ...formValidation };

        currentFields.forEach((field) => {
            _formValidation[field.name] = { valid: true, help: "" };

            if (field.validation && field.required) {
                const regex = new RegExp(field.validation.regex);
                const isValid = regex.test(String(formData[field.name]));
                _formValidation[field.name] = {
                    valid: isValid,
                    help: isValid ? "" : field.validation.regexMsg
                };
            }

            if (field.required && !formData[field.name]) {
                _formValidation[field.name] = { valid: false, help: field.validation ? field.validation.regexMsg : "Campo obrigatório!" };
            }
        });

        const stepIsValid = currentFields.every(field => _formValidation[field.name]?.valid);
        setFormValidation(_formValidation);
        return stepIsValid;
    };

    const handleWizardStepNext = () => {
        if (validateCurrentStep()) {
            setActiveStep((prev) => prev + 1);
        } else {
            // Optionally, display a message to the user indicating there are validation errors.
            //    alert("Please correct the errors before proceeding.");
        }
    };
    // const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setDirtyFields(prev => ({ ...prev, [e.target.name]: true }));
    };
    const handleDropdownFieldChange = (e: { label: string, selected: boolean, value: any }, fieldName: string) => setFormData(prev => ({ ...prev, [fieldName]: e.value }));

    const handleCEPChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        const cepAPI = axios.create({ baseURL: 'https://viacep.com.br/ws/' });

        const fillAddress = (logradouro: string, bairro: string, localidade: string, uf: string) => {
            props.formFields.fieldgroups.forEach(fg => {
                fg.fields.forEach(f => {
                    if (f.addressFieldFromCEP && f.addressFieldFromCEP == 'logradouro')
                        setFormData(prev => ({ ...prev, [f.name]: logradouro }))
                    if (f.addressFieldFromCEP && f.addressFieldFromCEP == 'bairro')
                        setFormData(prev => ({ ...prev, [f.name]: bairro }))
                    if (f.addressFieldFromCEP && f.addressFieldFromCEP == 'localidade')
                        setFormData(prev => ({ ...prev, [f.name]: localidade }))
                    if (f.addressFieldFromCEP && f.addressFieldFromCEP == 'uf')
                        setFormData(prev => ({ ...prev, [f.name]: uf }))
                })
            })
        }

        handleFieldChange(e);

        var _cep = Number(String(e.target.value).replace(/[^0-9]/, ''));

        if (_cep !== null && _cep > 0 && _cep.toString().length == 8) {
            timeoutToken !== undefined && clearTimeout(timeoutToken);
            setLoadingCEP(true);
            let _token = setTimeout(() => {
                cepAPI.get(`${_cep}/json`)
                    .then(result => {
                        if (result.status == 200 && !result.data.hasOwnProperty('erro')) {
                            let _data: ConsultaCEP = result.data;
                            fillAddress(_data.logradouro, _data.bairro, _data.localidade, _data.uf);
                        }
                    })
                    .catch(error => console.error(error))
                    .then(() => setLoadingCEP(false));
            }, 3000);
            setTimeoutToken(_token);
        } else {
            fillAddress("", "", "", "");
            setLoadingCEP(false)
        }
    }

    const handleFormCancel = () => {
        setFormData({});
        setFormValidation([]);
        setFormIsValid(false);
        setActiveStep(0);
        props.onCancel();
    }

    const handleModalClose = () => {
        setFormData({});
        setFormValidation([]);
        setFormIsValid(false);
        setActiveStep(0);
        props.onClose();
    }

    useEffect(() => {
        const execute = async () => {
            setLoading(true);
            await props.onOpen();
            setLoading(false);
        }

        props.show && execute();

    }, [props.show]);

    useEffect(() => {
        if (props.mode === 'edit')
            setFormData(props.data)
    }, [props.data]);

    //const handleWizardStepNext = () => setActiveStep((prev) => prev + 1);
    const handleWizardStepBack = () => setActiveStep((prev) => prev - 1);

    const renderWizardFormReview = () => {
        let _allFields: Field[] = [];
        props.formFields.fieldgroups.forEach(g => g.fields.forEach(f => _allFields.push(f)));

        return <>
            {
                _allFields.map((field: Field, fi: number) => {
                    let _value: any = formData[field.name]; 
                    if (field.type === 'password') {
                        _value = '*****';
                    }
                    if (field.type === 'checkbox') {
                        _value = _value == true ? 'Sim' : 'Não';
                    } 
                    var displayValue = field.options && field.options.length > 0 ? field.options.filter(x => x.value == _value)[0].label : _value;
                     
                    return (
                        <div key={fi}>
                            <span><strong>{field.label}</strong>: {displayValue}</span>
                        </div>
                    )
                })
            }
        </>
    }

    // Para remover os números dos ícones do Wizard
    const useStyles = makeStyles((theme) => ({
        stepper: {
            "& .MuiStepIcon-text": {
                display: "none",
            },
        },
    }));
    const classes = useStyles();

    const renderForm = () => {
        if (props.type === "fieldset")
            return props.formFields.fieldgroups.map((group: FieldGroup, fsi: number) => {
                return group.accordion ? (
                    <LcAccordion key={fsi} title={group.label} visible={true}>
                        <AccordionDetails className='formModalFieldset'>
                            {renderFields(group.fields)}
                        </AccordionDetails>
                    </LcAccordion>
                ) : (
                    <Box component='fieldset' key={fsi} className='formModalFieldset'>
                        {group.label && <legend className='formModalFieldsetTitle'>{group.label}</legend>}
                        {renderFields(group.fields)}
                    </Box>
                )
            });

        if (props.type === "wizard")
            return <>
                <Stepper alternativeLabel activeStep={activeStep} className={classes.stepper}>
                    {
                        props.formFields.fieldgroups.map((group: FieldGroup, fgi: number) => (
                            <Step key={`${fgi}_${group.label}`}>
                                <StepLabel>{group.label}</StepLabel>
                            </Step>
                        ))
                    }
                    <Step key="stepper_completed">
                        <StepLabel>Revisar e Salvar</StepLabel>
                    </Step>
                </Stepper>
                <div className='formModalWizardFormArea'>
                    {
                        activeStep < props.formFields.fieldgroups.length &&
                        <>
                            <div className='formModalWizardFieldGroup'>
                                {renderFields(props.formFields.fieldgroups[activeStep].fields)}
                            </div>
                            <div></div>
                        </>
                    }
                    {
                        activeStep == props.formFields.fieldgroups.length &&
                        <div className='formModalWizardReviewArea'>
                            <div className='formModalWizardReviewDesc'> Revise os dados e confirme antes de salvar! Caso deseje alterar algo, clique em "Voltar".</div>
                            <div className='formModalWizardReviewFields'>{renderWizardFormReview()}</div>
                            <div className='formModalWizardReviewButtons'>
                                <button className="lc-button bg-info mr-6" type="button" onClick={handleFormSave} disabled={!formIsValid}>Salvar</button>
                            </div>
                        </div>

                    }
                    <div className='formModalWizardButtons'>
                        <button
                            className="lc-button bg-gray"
                            type="button"
                            hidden={activeStep === 0}
                            disabled={activeStep === 0}
                            onClick={handleWizardStepBack}
                            style={{ float: 'left' }}
                        >
                            Voltar
                        </button>
                        <button
                            className="lc-button bg-info mr-6"
                            type="button"
                            hidden={activeStep === props.formFields.fieldgroups.length}
                            disabled={activeStep === props.formFields.fieldgroups.length}
                            onClick={handleWizardStepNext}
                            style={{ float: 'right' }}
                        >
                            Próximo
                        </button>
                    </div>
                </div >
            </>
    }

    const renderFields = (fields: Field[]) => {
        return fields.map((field: Field, fi: number) => {
            return (
                <div>
                    <PPTooltip content={String(field.tooltip)} trigger='hover'>
                        {
                            ["text", "password", "number"].includes(field.type) &&
                            <PPInput
                                key={`${fi}_${field.name}`}
                                type={field.type as "text" | "password" | "number"}
                                name={field.name}
                                title={field.label}
                                value={formData[field.name] as string}
                                hidden={field.hidden || (props.mode === "edit" && field.blockEdit)}
                                disabled={field.disabled}
                                required={field.required}
                                onChange={handleFieldChange}
                                width={field.width}
                                error={formValidation[field.name] && {
                                    show: !formValidation[field.name].valid as boolean,
                                    help: formValidation[field.name].help as string
                                }}
                            />
                        }
                        {
                            field.type == "CEP" &&
                            <LcLoading loading={loadingCEP} label="Buscando CEP...">
                                <PPInput
                                    key={`${fi}_${field.name}`}
                                    type="number"
                                    name={field.name}
                                    title={field.label}
                                    value={formData[field.name] as number}
                                    hidden={field.hidden || (props.mode === "edit" && field.blockEdit)}
                                    disabled={field.disabled}
                                    required={field.required}
                                    onChange={handleCEPChange}
                                    width={field.width}
                                    error={field.required ? formValidation[field.name] && {
                                        show: formValidation[field.name].valid as boolean,
                                        help: formValidation[field.name].help as string
                                    } : {
                                        show: false,
                                        help: null
                                    }
                                    }
                                />
                            </LcLoading>
                        }
                        {
                            field.type == "textarea" &&
                            <PPTextArea
                                key={`${fi}_${field.name}`}
                                name={field.name}
                                title={field.label}
                                value={formData[field.name] as string}
                                hidden={field.hidden || (props.mode === "edit" && field.blockEdit)}
                                disabled={field.disabled}
                                required={field.required}
                                onChange={handleFieldChange}
                                width={String(field.width)}
                                error={formValidation[field.name] && {
                                    show: !formValidation[field.name].valid as boolean,
                                    help: formValidation[field.name].help as string
                                }}
                            />
                        }
                        {
                            field.type == "checkbox" &&
                            <PPCheckBox
                                key={`${fi}_${field.name}`}
                                name={field.name}
                                title={field.label}
                                checked={formData[field.name] as boolean}
                                disabled={field.disabled || (props.mode === "edit" && field.blockEdit)}
                                onChange={handleFieldChange}
                            />
                        }
                        {
                            field.type == "dropdown" &&
                            <PPDropDown
                                key={`${fi}_${field.name}`}
                                name={field.name}
                                title={field.label}
                                handleMenuOpen={field.handleMenuOpen}
                                value={field.options?.find(o => o.value === formData[field.name] as string)}
                                options={field.options ?? []}
                                disabled={field.disabled || (props.mode === "edit" && field.blockEdit)}
                                required={field.required}
                                onChange={(e) => handleDropdownFieldChange(e, field.name)}
                                width={`${field.width}px`}
                                maxMenuHeight={90}
                                error={formValidation[field.name] && {
                                    show: !formValidation[field.name].valid as boolean,
                                    help: formValidation[field.name].help as string
                                }}
                            />
                        }
                    </PPTooltip>
                </div>
            )
        })
    }

    return (
        <PPModal title={props.title} visible={props.show} onClose={handleModalClose} size={props.size} functions={[
            { label: "Salvar", onClick: handleFormSave, disabled: !formIsValid, hidden: props.type === "wizard" },
            { label: "Cancelar", onClick: handleFormCancel, disabled: false, hidden: props.type === "wizard" }
        ]}>
            <LcLoading loading={loading} label="Carregando dados do formulário, aguarde...">
                {props.desc && <div className="formModalDescription">{props.desc}</div>}
                {renderForm()}
            </LcLoading>
        </PPModal>
    );
}

export default PPFormModal;